import { api, loginInput, type LoginInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, FormGroup, Input } from '../ui';

export function Login() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
    defaultValues: { identifier: '', password: '' },
  });

  const login = useMutation({
    mutationFn: (values: LoginInput) => api.auth.adminLogin(values),
    onSuccess: (result) => {
      // A TOTP challenge means no session cookie has been issued yet.
      navigate(result.requires_two_factor ? '/two-factor' : '/', { replace: true });
    },
    onError: (error: Error) => setFormError(error.message),
  });

  return (
    <div className="auth-wrap">
      <Card className="auth-card">
        <h1 className="auth-title">Alpha One Admin</h1>
        <p className="auth-subtitle">Sign in to the administration panel</p>

        {formError ? <Alert tone="danger">{formError}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => login.mutate(values))} noValidate>
          <FormGroup label="User ID or Email" error={form.formState.errors.identifier?.message}>
            <Input autoComplete="username" {...form.register('identifier')} />
          </FormGroup>

          <FormGroup label="Password" error={form.formState.errors.password?.message}>
            <Input type="password" autoComplete="current-password" {...form.register('password')} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={login.isPending} className="w-100">
            {login.isPending ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
